import { useEffect, useLayoutEffect } from 'react';
import { Outlet, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from 'src/hooks/auth';
import { logger, analytics, RUM } from 'src/services';
import { useChatValidationStore } from 'src/hooks/chat/useChatValidationStore';
import { AppLoader, ErrorPage } from 'src/components';
import {
  getAppointmentDetailsForAnalytics,
  getPractitionerName,
} from 'src/utils';
import { PatientAuthForm } from './patientAuth';
import { RootLayout } from '../root';
import ConditionSelector from './conditionSelector';
import { useTranslation } from 'react-i18next';

const REDIRECT_URL_KEY = 'FRONTEGG_AFTER_AUTH_REDIRECT_URL';

export const Authenticated = () => {
  const { chatToken } = useParams() as { chatToken: string }; // Get access token from url
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  const { t: tMisc } = useTranslation('misc');

  const {
    isAuthenticated,
    user,
    isPractitioner,
    authenticate,
    isLoading,
    validateLoggedInUser,
    appointment,
    showConditionSelector,
  } = useAuth();

  const {
    validateChatToken,
    loading: loadingChatValidation,
    chat,
    error: chatValidationError,
  } = useChatValidationStore();

  useLayoutEffect(() => {
    if (chatToken) {
      validateChatToken(chatToken);
    } else {
      logger.error('Auth: No chatToken found in the url', {
        chatToken,
        url: window.location.href,
      });
    }
  }, []);

  useLayoutEffect(() => {
    if (chat && !chatValidationError) {
      localStorage.setItem(
        REDIRECT_URL_KEY,
        `${window.location.pathname}${window.location.search}`
      );
      authenticate();
    }
    // Authenticate only if chat is validated
  }, [!!chat]);

  useEffect(() => {
    if (isAuthenticated) {
      analytics.track('Authentication success on Lumi V2');
      if (isPractitioner) {
        const redirectUrl = localStorage.getItem(REDIRECT_URL_KEY);
        localStorage.removeItem(REDIRECT_URL_KEY);
        if (redirectUrl) {
          navigate(redirectUrl);
        }
        if (user) {
          RUM.setUser({
            id: user.id,
            name: user.name,
            email: user.email,
          });
          analytics.identify(user.id, {
            email: user.email,
            name: user.name,
          });
        } else {
          logger.warn('Autheticated user not found');
        }
        validateLoggedInUser({ chatToken });
      } else {
        if (appointment) {
          analytics.track('Authenticate Patient Lumi Chat', {
            ...getAppointmentDetailsForAnalytics(appointment),
            authenticate_success: 'True',
          });
          analytics.track(
            'Autheticated on Lumi V2',
            getAppointmentDetailsForAnalytics(appointment)
          );
          RUM.setUser({
            name: `ApptID: ${appointment?.appointment_id}`,
            practitioner_name: getPractitionerName(appointment),
            appointment_id: appointment?.appointment_id,
          });
        } else {
          logger.error('User autheticated without appointment', {
            chatToken,
          });
        }
      }

      const urlParams = new URLSearchParams(window.location.search);
      urlParams.set('success', 'v2');
      const successUrl = `${window.location.pathname}?${urlParams.toString()}`;
      navigate(successUrl);
    }
  }, [isAuthenticated]);

  if (chatValidationError) {
    const { code } = chatValidationError;
    if (code === 403) {
      return <ErrorPage description={tMisc('403.description')} />;
    }

    if (code === 404) {
      return (
        <ErrorPage
          code={404}
          title={tMisc('404.title')}
          description={tMisc('404.description')}
        />
      );
    }

    return (
      <ErrorPage
        title={t('errors.expiredLink.title')}
        description={t('errors.expiredLink.description')}
      />
    );
  }

  if (loadingChatValidation || isLoading) {
    return <AppLoader />;
  }

  if (!isAuthenticated) {
    if (isPractitioner) {
      logger.error('Practitioner silently denied access', {
        chatToken,
      });
    }
    return <PatientAuthForm chatToken={chatToken} />;
  }

  if (showConditionSelector) {
    return <ConditionSelector open />;
  }

  return (
    <RootLayout>
      <Outlet />
    </RootLayout>
  );
};
